import { useEffect, useState, useRef, useContext } from "react";
import ToolbarTop from "./Toolbar";
import { Field as BaseField } from "@base-ui-components/react/field";
import { Select } from "@base-ui-components/react/select";
import { Popconfirm, Table } from "antd";
import {
  get_attributes_by_access_code,
  get_interpreters_by_language,
  submit_call_attributes,
} from "../../api/CallViewApi";
import { Fieldset } from "@base-ui-components/react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Separator } from "../ui/separator";
import { get_customer_info } from "../../api/CustomerApi";
import { GlobalContext } from "../../App";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import * as Sentry from "@sentry/react";
const { logger } = Sentry;
function ChevronUpDownIcon() {
  return (
    <svg
      width="8"
      height="12"
      viewBox="0 0 8 12"
      fill="none"
      stroke="currentcolor"
      strokeWidth="1.5"
    >
      <path d="M0.5 4.5L4 1.5L7.5 4.5" />
      <path d="M0.5 7.5L4 10.5L7.5 7.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg fill="currentcolor" width="10" height="10" viewBox="0 0 10 10">
      <path d="M9.1603 1.12218C9.50684 1.34873 9.60427 1.81354 9.37792 2.16038L5.13603 8.66012C5.01614 8.8438 4.82192 8.96576 4.60451 8.99384C4.3871 9.02194 4.1683 8.95335 4.00574 8.80615L1.24664 6.30769C0.939709 6.02975 0.916013 5.55541 1.19372 5.24822C1.47142 4.94102 1.94536 4.91731 2.2523 5.19524L4.36085 7.10461L8.12299 1.33999C8.34934 0.993152 8.81376 0.895638 9.1603 1.12218Z" />
    </svg>
  );
}

const columns = [
  {
    title: "Interpreter Name",
    dataIndex: "interpreter_first_name",
    render: (text, record) =>
      `${record.interpreter_first_name} ${record.interpreter_last_name}`,
    sorter: (a, b) =>
      a.interpreter_first_name.length - b.interpreter_first_name.length,
  },
  { title: "Interpreter Number", dataIndex: "interpreter_phone_number" },
  { title: "Interpreter Tier", dataIndex: "user_tier" },
  {
    title: "Interpreter Status",
    dataIndex: "current_status",
    render: (text) =>
      text === "Ready" ? (
        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
          {text}
        </span>
      ) : text === "Occupied" ? (
        <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
          {text}
        </span>
      ) : (
        <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-red-600/10 ring-inset">
          {text}
        </span>
      ),
  },
];

// Validation functions
const validateField = (value, isRequired) => {
  if (isRequired && (!value || value.trim() === "")) {
    return "This field is required";
  }
  return "";
};

export default function CallView({
  callInfo,
  setFormSubmitted,
  setCallEvent,
  setCallInfo,
  callEvent,
  user,
}) {
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerAttributes, setCustomerAttributes] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [attrs, setAttrs] = useState({});
  const [accessCode, setAccessCode] = useState("");
  const [language, setLanguage] = useState("");
  const [openSections, setOpenSections] = useState({
    customer: false,
    interpreter: false,
    language: false,
    department: false,
  });
  const [attrPopoverOpen, setAttrPopoverOpen] = useState({});
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const getAttributesByAccessCode = async (
    accessCode,
    dialedNumber,
    engagement_id
  ) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsLoadingAttributes(true);
      setFormSubmitted(false);
      setAttrs({});
      setFormValues({});
      const response = await get_attributes_by_access_code(
        accessCode,
        dialedNumber,
        engagement_id,
        { signal: controller.signal }
      );

      // Clear the abort controller if the request was successful
      if (!controller.signal.aborted) {
        abortControllerRef.current = null;
      }

      // Clear loading state
      setIsLoadingAttributes(false);

      const attributes = response.data.attributes || [];
      setCustomerAttributes(attributes);

      // Extract category code data with proper type checking
      const categoryCodeName =
        response.data.category_code_name || callInfo?.category_code_name || "";
      const categoryCodeValue =
        response.data.category_code_value ||
        callInfo?.category_code_value ||
        "";
      const categoryCodeValues = response?.data?.category_code_values || [];

      // Preserve existing callInfo properties while updating with new data
      const updatedCallInfo = {
        ...callInfo, // Spread existing callInfo first to preserve all properties
        category_code_name: categoryCodeName,
        category_code_values: categoryCodeValues,
        category_code_value: categoryCodeValue,
        notes: response.data.notes ?? callInfo?.notes ?? "",
        script: response.data.script ?? callInfo?.script ?? "",
        customer_name:
          response.data.customer_name ?? callInfo?.customer_name ?? "",
        // Preserve the caller_number from the original callInfo if it exists
        caller_number:
          response.data.caller_number ?? callInfo?.caller_number ?? "",
        flow_name: response.data.flow_name ?? callInfo?.flow_name ?? "",
        is_changed_by_agent: false,
      };

      setCallInfo(updatedCallInfo);
    } catch (error) {
      console.error("Error getting attributes by access code:", error);
      if (error.name !== "AbortError") {
        // Only show error if it's not an abort error
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage("Failed to load attributes. Please try again.");
      }
      setIsLoadingAttributes(false);
      setCustomerAttributes([]);
      setToastMessage("Failed to load attributes. Please try again.");
      throw error;
    }
  };

  const getInterpretersByLanguage = async (language) => {
    setLoading(true);
    try {
      let response = await get_interpreters_by_language(language);
      setInterpreters(response || []);
    } catch (error) {
      console.error("Error fetching interpreters:", error);
      setInterpreters([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Function to update interpreter status when receiving WebSocket events
  const updateInterpreterStatus = (
    zUserId,
    currentStatus,
    currentSubStatus
  ) => {
    if (!zUserId || !currentStatus) return;

    setInterpreters((prevInterpreters) => {
      // Create a copy of the interpreters array
      const updatedInterpreters = [...prevInterpreters];

      // Find the interpreter with the matching z_user_id
      const interpreterIndex = updatedInterpreters.findIndex(
        (interpreter) => interpreter.z_user_id === zUserId
      );

      // If found, update the status
      if (interpreterIndex !== -1) {
        updatedInterpreters[interpreterIndex] = {
          ...updatedInterpreters[interpreterIndex],
          current_status: currentStatus,
          current_sub_status: currentSubStatus || null,
        };
        console.log(
          `Updated interpreter ${zUserId} status to ${currentStatus}`
        );
      }

      return updatedInterpreters;
    });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleFormSubmit = async () => {
    console.log("Current callInfo:", callInfo);

    // Validate form
    const errors = {};

    // Validate required attributes
    if (customerAttributes.length > 0) {
      customerAttributes.forEach((attr) => {
        if (attr.is_input === 1 && attr.is_required === 1) {
          const value = attrs[attr.attribute_id];
          const error = validateField(value, true);
          if (error) {
            errors[`attrs.${attr.attribute_id}`] = error;
          }
        }
      });
    }

    // If there are validation errors, update state and return
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage("Please fill in all required fields");
      return;
    }

    try {
      const engagementId = callInfo?.engagement_id;
      if (!engagementId) {
        throw new Error("Missing engagement ID");
      }

      console.log("Using engagement ID:", engagementId);

      // Get the latest category code values from callInfo
      const categoryCodeName = callInfo?.category_code_name || "";
      const categoryCodeValue = callInfo?.category_code_value || "";

      console.log("Category code name:", categoryCodeName);
      console.log("Category code value:", categoryCodeValue);

      // Filter attributes to only include those that correspond to input fields
      const filteredAttrs = {};

      // Only include attributes where the corresponding customerAttribute has is_input=1
      if (customerAttributes.length > 0) {
        customerAttributes.forEach((attr) => {
          if (attr.is_input === 1 && attrs[attr.attribute_id] !== undefined) {
            filteredAttrs[attr.attribute_id] = attrs[attr.attribute_id];
          }
        });
      }

      console.log("Filtered attributes:", filteredAttrs);

      const formattedValues = {
        ...formValues,
        attrs: filteredAttrs,
        accessCode: accessCode || "",
        language: language || "", // Add language to the request body
      };

      const response = await submit_call_attributes(
        formattedValues,
        engagementId,
        accessCode || "",
        categoryCodeName,
        categoryCodeValue,
        language || ""
      );

      console.log("Form submission response:", response);

      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Form submitted successfully!");
      if (callEvent === "ENDED" || callEvent === "IDLE") {
        setFormSubmitted(true); // Set formSubmitted to true to trigger the transition to CallLogs
      }
    } catch (error) {
      console.error("Error saving form data:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Failed to submit form: " + (error.message || "Unknown error")
      );
      throw error;
    }
  };

  const handleCallInfo = async (engagement_id) => {
    setFormSubmitted(false);
    try {
      const response = await get_customer_info(engagement_id);

      if (response && response.data) {
        // Set access code and language
        const accessCodeValue = response.data.access_code || "";
        const languageValue = response.data.language || "";

        setAccessCode(accessCodeValue);
        setLanguage(languageValue);

        // Preserve existing category codes if they're not in the response
        const updatedCallInfo = {
          ...callInfo,
          ...response.data,
          category_code_name:
            response.data.category_code_name ||
            callInfo.category_code_name ||
            "",
          category_code_value:
            response.data.category_code_value ||
            callInfo.category_code_value ||
            "",
          category_code_values:
            response.data.category_code_values ||
            callInfo.category_code_values ||
            [],
        };

        setCallInfo(updatedCallInfo);

        // Do not fetch here; rely on the debounced effect triggered by accessCode
      } else {
        console.error("Invalid response format from get_customer_info");
      }
    } catch (error) {
      console.error("Error fetching customer info:", error);
      throw error;
    }
  };

  // Function to reset all form-related state between calls
  const resetFormState = () => {
    setSelectedRowKeys([]);
    setCustomerAttributes([]);
    setInterpreters([]);
    setFormValues({});
    setFormErrors({});
    setAttrs({});
    setAccessCode("");
    setLanguage("");
    setOpenSections({
      customer: false,
      interpreter: false,
      language: false,
      department: false,
    });
  };

  useEffect(() => {
    if (callInfo && callInfo.engagement_id) {
      // Reset all form state when engagement_id changes (new call)
      // resetFormState();
      handleCallInfo(callInfo.engagement_id);
    }
  }, [callInfo?.engagement_id]);

  useEffect(() => {
    console.log("callEvent changed:", callEvent);
    if (callEvent == "ENDED") {
      // Submit the form and then reset state
      handleFormSubmit();
      // Reset form state immediately to ensure it's clean for the next call
      resetFormState();
    }
  }, [callEvent]);

  useEffect(() => {
    const key = import.meta.env.VITE_PUSHER_KEY;
    const host = import.meta.env.VITE_WS_HOST;
    const port = import.meta.env.VITE_WS_PORT;

    // WebSocket connection variables
    let ws;
    let pingInterval;
    let reconnectTimeout;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    const baseReconnectDelay = 1000; // Start with 1 second

    // Function to create and setup the WebSocket connection
    const setupWebSocket = () => {
      // Clear any existing reconnect timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      ws = new WebSocket(
        `${host}:${port}/app/${key}?protocol=7&client=js&version=4.3.1&flash=false`
      );

      ws.onopen = () => {
        console.log("WebSocket connection opened");
        // logger.info("WebSocket connection opened");
        // Reset reconnect attempts on successful connection
        reconnectAttempts = 0;

        // Send the correct Pusher subscription message format
        ws.send(
          JSON.stringify({
            event: "pusher:subscribe",
            data: {
              channel: "ws-events",
            },
          })
        );

        // Set up ping interval to keep connection alive (every 20 seconds)
        if (pingInterval) clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: "pusher:ping" }));
            console.log("Ping sent to keep WebSocket alive");
          }
        }, 20000); // 20 seconds interval to prevent 30-second timeout
      };

      ws.onmessage = (msg) => {
        const payload = JSON.parse(msg.data);
        console.log("WebSocket message:", payload);
        // logger.info("WebSocket message:", payload);
        try {
          const eventData = JSON.parse(payload.data);
          if (eventData && eventData.z_user_id) {
            // Update interpreter status in the interpreters array
            updateInterpreterStatus(
              eventData.z_user_id,
              eventData.current_status,
              eventData.current_sub_status
            );
          }
        } catch (error) {
          console.error("Error processing ws-events message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        // logger.error("WebSocket error:", error);
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed with code:", event.code);
        clearInterval(pingInterval);

        // Don't attempt to reconnect if we're unmounting (component cleanup)
        if (!event.wasClean && reconnectAttempts < maxReconnectAttempts) {
          // Calculate delay with exponential backoff (1s, 2s, 4s, 8s, etc.)
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
          console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${
              reconnectAttempts + 1
            }/${maxReconnectAttempts})`
          );

          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            setupWebSocket();
          }, delay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          console.error(
            "Maximum reconnection attempts reached. Please refresh the page."
          );
          // logger.error(
          //   "Maximum reconnection attempts reached. Please refresh the page."
          // );
        }
      };
    };

    // Initial WebSocket setup
    setupWebSocket();

    // Cleanup function when component unmounts
    return () => {
      console.log("Cleaning up WebSocket connection");
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Set a flag or use a clean close code to indicate intentional closure
        ws.close(1000, "Component unmounting"); // 1000 is normal closure
      }
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user]);

  useEffect(() => {
    language && getInterpretersByLanguage(language);
  }, [language]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending debounce timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounced effect for access code changes
  useEffect(() => {
    if (accessCode) {
      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        getAttributesByAccessCode(
          accessCode,
          callInfo.dialed_number,
          callInfo.engagement_id
        );
      }, 800); // 800ms debounce delay - allows for complete typing
    }

    // Cleanup function to clear the timer
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [accessCode, callInfo.dialed_number, callInfo.engagement_id]);

  return (
    <>
      <ToolbarTop callInfo={callInfo} />
      <div>
        <Accordion type="multiple" className="w-full" defaultValue={[]}>
          <AccordionItem value="item-1">
            <AccordionTrigger>Notes</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <div dangerouslySetInnerHTML={{ __html: callInfo?.notes }}></div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>Script</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <div dangerouslySetInnerHTML={{ __html: callInfo?.script }}></div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="flex flex-col">
        {/* Form content */}
        <div className="flex flex-1 flex-col gap-10 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <BaseField.Root className="flex w-full flex-col items-start gap-1">
              <BaseField.Label className="text-sm font-medium text-gray-900">
                Access Code
              </BaseField.Label>
              <input
                value={accessCode || ""}
                placeholder="Required"
                className="h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800"
                onChange={(e) => {
                  const value = e.target.value;
                  setAccessCode(value);
                  setFormValues((prev) => ({ ...prev, accessCode: value }));
                }}
              />
              <div className="flex items-center gap-2 mt-1">
                {isLoadingAttributes && (
                  <div className="flex items-center text-sm text-blue-600">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading attributes...
                  </div>
                )}
                {formErrors.accessCode && (
                  <BaseField.Error className="text-sm text-red-800">
                    {formErrors.accessCode}
                  </BaseField.Error>
                )}
              </div>
            </BaseField.Root>
            <div className="flex flex-col">
              <label className="text-sm text-muted-foreground mb-1">
                Language
              </label>
              <Popover
                open={openSections.language}
                onOpenChange={(val) =>
                  setOpenSections((prev) => ({ ...prev, language: val }))
                }
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openSections.language}
                    className="justify-between cursor-pointer"
                  >
                    <span className="line-clamp-1">
                      {language ? language : "Select language..."}
                    </span>
                    <ChevronsUpDown className="opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput placeholder="Search language..." />
                    <CommandList>
                      <CommandEmpty>No languages found.</CommandEmpty>
                      <CommandGroup>
                        {languages.map((lang) => (
                          <CommandItem
                            key={lang.name}
                            defaultValue={lang.name}
                            className={"cursor-pointer"}
                            onSelect={() => {
                              setLanguage(lang.name);
                              setOpenSections((prev) => ({
                                ...prev,
                                language: false,
                              }));
                            }}
                          >
                            {lang.name}
                            <span>-</span>
                            <span className="text-muted-foreground">
                              {lang.code}
                            </span>
                            {language === lang.name && <CheckIcon />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <BaseField.Root className="flex w-full flex-col items-start gap-1">
              <div className="flex flex-col gap-2 w-full">
                <BaseField.Label className="text-sm font-medium text-gray-900 ">
                  {callInfo?.category_code_name}{" "}
                </BaseField.Label>
                <div className="flex flex-col gap-2 w-full">
                  {(() => {
                    const hasValidCategoryName =
                      Boolean(callInfo?.category_code_name?.trim()) &&
                      callInfo?.category_code_name !== "null";

                    if (
                      hasValidCategoryName &&
                      Array.isArray(callInfo?.category_code_values) &&
                      callInfo.category_code_values.length > 0
                    ) {
                      return (
                        <Select.Root
                          value={callInfo?.category_code_value || ""}
                          onValueChange={(value) =>
                            setCallInfo((prevCallInfo) => ({
                              ...prevCallInfo,
                              category_code_value: value || "",
                              is_changed_by_agent: true,
                            }))
                          }
                          disabled={
                            callInfo?.category_code_value != "" &&
                            callInfo?.category_code_value != null &&
                            !callInfo?.is_changed_by_agent
                          }
                          defaultValue={callInfo?.category_code_value || ""}
                          className="w-full"
                        >
                          <Select.Trigger
                            className={`h-10 w-full rounded-md border border-gray-200 pl-3.5 pr-2 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800 flex items-center justify-between ${
                              callInfo?.category_code_value != "" &&
                              callInfo?.category_code_value != null &&
                              !callInfo?.is_changed_by_agent
                                ? "cursor-not-allowed opacity-50"
                                : ""
                            }`}
                          >
                            <Select.Value
                              placeholder={
                                callInfo?.category_code_value ||
                                "Select a value"
                              }
                            />
                            <Select.Icon className="flex">
                              <ChevronUpDownIcon />
                            </Select.Icon>
                          </Select.Trigger>
                          <Select.Portal>
                            <Select.Positioner
                              className="outline-none"
                              sideOffset={8}
                            >
                              <Select.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                              <Select.Popup className="group [max-height:var(--available-height)] origin-[var(--transform-origin)] overflow-y-auto rounded-md bg-[canvas] py-1 text-gray-900 shadow-lg shadow-gray-200 outline-1 outline-gray-200 transition-[transform,scale,opacity]  data-[ending-style]:scale-100 data-[ending-style]:opacity-100 data-[ending-style]:transition-none data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[starting-style]:transition-none data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none">
                                {callInfo.category_code_values.map((value) => (
                                  <Select.Item
                                    className="grid min-w-[var(--anchor-width)] cursor-default grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900"
                                    value={value}
                                    key={value}
                                  >
                                    <Select.ItemIndicator className="col-start-1">
                                      <CheckIcon className="size-3" />
                                    </Select.ItemIndicator>
                                    <Select.ItemText className="col-start-2">
                                      {value}
                                    </Select.ItemText>
                                  </Select.Item>
                                ))}
                              </Select.Popup>
                              <Select.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                            </Select.Positioner>
                          </Select.Portal>
                        </Select.Root>
                      );
                    } else if (hasValidCategoryName) {
                      return (
                        <BaseField.Control
                          value={callInfo?.category_code_value || ""}
                          onChange={(e) =>
                            setCallInfo({
                              ...callInfo,
                              category_code_value: e.target.value,
                            })
                          }
                          className="h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800"
                        />
                      );
                    } else {
                      return null;
                    }
                  })()}
                </div>
              </div>
            </BaseField.Root>
          </div>
          <Separator className={"mt-5"} />
          {customerAttributes.length > 0 && (
            <Fieldset.Root className="flex w-full flex-col gap-4">
              <Fieldset.Legend className="mb-2 text-lg font-medium text-gray-900">
                {customerAttributes[0].customer_name}
              </Fieldset.Legend>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {customerAttributes.map((data, idx) =>
                  data.is_input ? (
                    <BaseField.Root
                      className="flex w-full flex-col items-start gap-1"
                      key={idx}
                    >
                      <BaseField.Label className="text-sm font-medium text-gray-900">
                        {data.attribute_label || data.attribute_name}{" "}
                        {data.is_required == 1 && (
                          <span className="text-red-500 text-base">*</span>
                        )}
                      </BaseField.Label>
                      {data.attribute_type === "select" &&
                        (() => {
                          const optionsArray = Array.isArray(
                            data.attribute_options
                          )
                            ? data.attribute_options
                            : typeof data.attribute_options === "string" &&
                              data.attribute_options.length > 0
                            ? data.attribute_options
                                .split(",")
                                .map((s) => s.trim())
                                .filter((s) => s.length > 0)
                            : [];
                          return optionsArray.length > 0 ? (
                            <Popover
                              open={attrPopoverOpen[`attr-${idx}`] || false}
                              onOpenChange={(open) =>
                                setAttrPopoverOpen((prev) => ({
                                  ...prev,
                                  [`attr-${idx}`]: open,
                                }))
                              }
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={attrPopoverOpen[`attr-${idx}`]}
                                  className="w-full justify-between"
                                >
                                  <span className="line-clamp-1">
                                    {attrs[data.attribute_id] ||
                                      "Select value..."}
                                  </span>
                                  <ChevronsUpDown className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0">
                                <Command>
                                  <CommandInput placeholder="Search value..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No values found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {optionsArray.map((opt) => (
                                        <CommandItem
                                          key={opt}
                                          value={opt}
                                          onSelect={() => {
                                            const newAttrs = { ...attrs };
                                            newAttrs[data.attribute_id] = opt;
                                            setAttrs(newAttrs);
                                            setAttrPopoverOpen((prev) => ({
                                              ...prev,
                                              [`attr-${idx}`]: false,
                                            }));
                                            // Clear error when field is filled
                                            if (
                                              opt &&
                                              formErrors[
                                                `attrs.${data.attribute_id}`
                                              ]
                                            ) {
                                              const newErrors = {
                                                ...formErrors,
                                              };
                                              delete newErrors[
                                                `attrs.${data.attribute_id}`
                                              ];
                                              setFormErrors(newErrors);
                                            }
                                          }}
                                          className="cursor-pointer"
                                        >
                                          {opt}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          ) : null;
                        })()}
                      {!(data.attribute_type === "select") && (
                        <BaseField.Control
                          value={attrs[data.attribute_id] || ""}
                          placeholder={data.is_required == 1 ? "Required" : ""}
                          className="h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800"
                          onChange={(e) => {
                            const newAttrs = { ...attrs };
                            newAttrs[data.attribute_id] = e.target.value;
                            setAttrs(newAttrs);

                            // Clear error when field is filled
                            if (
                              e.target.value &&
                              formErrors[`attrs.${data.attribute_id}`]
                            ) {
                              const newErrors = { ...formErrors };
                              delete newErrors[`attrs.${data.attribute_id}`];
                              setFormErrors(newErrors);
                            }
                          }}
                        />
                      )}
                      {formErrors[`attrs.${data.attribute_id}`] && (
                        <BaseField.Error className="text-sm text-red-800">
                          {formErrors[`attrs.${data.attribute_id}`]}
                        </BaseField.Error>
                      )}
                    </BaseField.Root>
                  ) : (
                    <div key={idx} className="flex flex-col">
                      <h6>{data.attribute_name}</h6>
                      <p className="text-xs font-light text-gray-500">
                        {data.attribute_subtitle}
                      </p>
                    </div>
                  )
                )}
              </div>
            </Fieldset.Root>
          )}
        </div>

        <div className="mt-6 flex w-full">
          <button
            onClick={() => handleFormSubmit()}
            type="button"
            className="cursor-pointer rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 w-full"
          >
            Submit
          </button>
        </div>

        <div className="flex flex-1 flex-col mt-12">
          <div className="flex flex-row gap-2 mb-4 items-stretch">
            <span className="items-center rounded-md bg-gray-100 px-4 py-2 text-lg font-medium text-gray-600 w-full">
              {`${language} Interpreters`}
            </span>
            <button
              type="button"
              className="cursor-pointer rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
              onClick={() => language && getInterpretersByLanguage(language)}
            >
              Refresh
            </button>
          </div>
          <Table
            scroll={{ x: 1 }}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={interpreters}
            loading={loading}
          />
        </div>
      </div>
    </>
  );
}
